import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from '@tanstack/react-router';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { Input } from '@heyroot/ui/primitives/Input';
import { Skeleton } from '@heyroot/ui/primitives/Skeleton';
import {
  fetchAdminRole,
  updateAdminRole,
  deleteAdminRole,
  queryKeys,
  AdminApiError,
} from '../lib/api';
import type { PermName } from '../../rbac/permissions';
import { PermissionBitPicker } from '../components/roles/permission-bit-picker';
import { TypedConfirm } from '../components/typed-confirm';

const ERROR_COPY: Record<string, string> = {
  ROLE_IS_BUILTIN: 'Built-in roles cannot be edited or deleted.',
  ROLE_EXISTS: 'A role with that id already exists. Pick a different id.',
  UNKNOWN_PERMISSION: 'One of the selected permissions no longer exists. Reload the page.',
};

function copyFor(err: unknown, fallback: string): string {
  if (err instanceof AdminApiError) {
    if (err.code === 'ROLE_IN_USE') {
      // Fix round 2 finding 6: `assigneeCount` is what the server sends, but
      // treating an absent field as 0 would render "0 operators still have
      // this role" -- a sentence that contradicts the 409 it's explaining.
      // Fall back to a count-free sentence instead of guessing a number.
      const count = err.details['assigneeCount'];
      if (typeof count === 'number') {
        return `${count} operator${count === 1 ? '' : 's'} still have this role. Move them to another role first.`;
      }
      return 'Other operators still have this role. Move them to another role first.';
    }
    return ERROR_COPY[err.code] ?? err.message;
  }
  return fallback;
}

export function RoleDetailPage() {
  const { roleId } = useParams({ strict: false }) as { roleId: string };
  const navigate = useNavigate();
  const qc = useQueryClient();

  const {
    data: role,
    isLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.adminRole(roleId),
    queryFn: () => fetchAdminRole(roleId),
  });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [permissions, setPermissions] = useState<string[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // Fix round 2 finding 7: unguarded, this effect reseeds name/description/
  // permissions from the server on ANY refetch of `role` (e.g. TanStack
  // Query's default window-focus refetch), silently discarding an operator's
  // unsaved edits mid-edit. Only seed while the form hasn't been touched.
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (role && !touched) {
      setName(role.name);
      setDescription(role.description ?? '');
      setPermissions(role.permissions);
    }
  }, [role, touched]);

  function markEdited() {
    setTouched(true);
    setSaved(false);
  }

  const saveMutation = useMutation({
    mutationFn: (body: { name: string; description?: string; permissions: string[] }) =>
      updateAdminRole(roleId, body),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      // Un-touch: the form now matches what was just persisted, so the next
      // refetch (e.g. window-focus) reseeding from the server is a no-op, not
      // a discard.
      setTouched(false);
      void qc.invalidateQueries({ queryKey: queryKeys.adminRoles });
      void qc.invalidateQueries({ queryKey: queryKeys.adminRole(roleId) });
    },
    onError: (err: unknown) => setError(copyFor(err, 'Could not save this role.')),
  });

  const deleteMutation = useMutation({
    mutationFn: (reason: string) => deleteAdminRole(roleId, reason),
    onSuccess: () => {
      setError(null);
      setConfirmDelete(false);
      void qc.invalidateQueries({ queryKey: queryKeys.adminRoles });
      void navigate({ to: '/admin-roles' });
    },
    onError: (err: unknown) => setError(copyFor(err, 'Could not delete this role.')),
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-6 w-56" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError || !role) {
    return <EmptyState title="Role not found" description="This role may have been removed." />;
  }

  const readOnly = role.isBuiltin;

  return (
    <div className="flex flex-col gap-6 max-w-[900px]">
      <div>
        <Link to="/admin-roles" className="text-body-sm text-secondary hover:text-primary">
          Roles
        </Link>
      </div>

      <div className="flex items-center gap-2.5">
        <h1 className="text-heading-2 text-primary">{role.name}</h1>
        {readOnly && <Badge variant="neutral">Built-in</Badge>}
        <span className="text-body-sm text-muted">
          {role.assigneeCount} {role.assigneeCount === 1 ? 'operator' : 'operators'}
        </span>
      </div>

      {readOnly && (
        <p className="text-body-sm text-muted">
          Built-in roles cannot be edited or deleted, but their permissions are shown below so an
          operator can see exactly what this role grants.
        </p>
      )}

      {error && <p className="text-body-sm text-danger-text">{error}</p>}
      {saved && !error && <p className="text-body-sm text-success-text">Role saved.</p>}

      <div className="flex flex-col gap-4 max-w-xl">
        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm-emphasis text-secondary">Name</span>
          <Input
            value={name}
            disabled={readOnly}
            onChange={(e) => {
              setName(e.target.value);
              markEdited();
            }}
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm-emphasis text-secondary">Description</span>
          <Input
            value={description}
            disabled={readOnly}
            onChange={(e) => {
              setDescription(e.target.value);
              markEdited();
            }}
          />
        </label>
      </div>

      <section>
        <h2 className="text-eyebrow uppercase text-muted mb-3">Permissions</h2>
        <PermissionBitPicker
          selected={permissions}
          onChange={(next) => {
            setPermissions(next);
            markEdited();
          }}
          readOnly={readOnly}
        />
      </section>

      {!readOnly && (
        <div className="flex items-center gap-3">
          <Button
            onClick={async () => {
              try {
                // Success state set inline, not solely inside the mutation's
                // `onSuccess` -- a bare-spy `useMutation` test double never
                // invokes `onSuccess`, so this path must not depend on it to
                // be exercised (same reasoning as user-detail.tsx's password
                // reset, fix round 2 finding 8).
                // Key omitted rather than set to `undefined`: the branch
                // compiles with exactOptionalPropertyTypes, under which an
                // explicit `undefined` is not assignable to an optional prop.
                await saveMutation.mutateAsync({
                  name,
                  ...(description ? { description } : {}),
                  permissions: permissions as PermName[],
                });
                setError(null);
                setSaved(true);
                setTouched(false);
              } catch (err) {
                setError(copyFor(err, 'Could not save this role.'));
              }
            }}
            loading={saveMutation.isPending}
          >
            Save
          </Button>
          {/* Destructive action deliberately kept at text weight so it does not sit
              at equal visual weight with Save (Slice 2 follow-up 6). */}
          <Button
            variant="link"
            className="text-body-sm text-danger-text"
            onClick={() => {
              setError(null);
              setConfirmDelete(true);
            }}
          >
            Delete role
          </Button>
        </div>
      )}

      {!readOnly && (
        <TypedConfirm
          open={confirmDelete}
          title="Delete role"
          body={`This permanently removes the "${role.name}" role.`}
          confirmValue={roleId}
          confirmLabel="Delete"
          requireReason
          pending={deleteMutation.isPending}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={async (reason) => {
            try {
              await deleteMutation.mutateAsync(reason);
            } catch (err) {
              setError(copyFor(err, 'Could not delete this role.'));
            }
          }}
        />
      )}
    </div>
  );
}
